import { describeError as describeApiError, serverMessages } from '../../../utils/serverErrors';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';

export type ReviewAction = 'approve' | 'reject';

/** What a page shows once a review ends, after which it reloads the model. */
export interface ReviewOutcome {
  type: 'success' | 'error';
  message: string;
}

// Pricing's limit; the portal web service refuses a longer comment.
export const MAX_COMMENT_LENGTH = 500;

const ACCESS_DENIED_MESSAGE = 'You do not have permission to review change sets.';
const NO_LONGER_PENDING_MESSAGE = 'The change set is no longer pending.';
export const NOT_FOUND_MESSAGE = 'The change set was not found.';

export const changeSetReviewPath = (changeSetId: string, action: ReviewAction): string =>
  `/changeSets/${encodeURIComponent(changeSetId)}/${action}`;

/** The length the comment is sent with, as `toRejectRequest` trims it. */
export const commentLength = (comment: string): number => comment.trim().length;

export const validateComment = (comment: string): string | undefined =>
  commentLength(comment) > MAX_COMMENT_LENGTH
    ? `Use at most ${MAX_COMMENT_LENGTH} characters.`
    : undefined;

/** A blank comment is left out, since the portal web service refuses an empty one. */
export const toRejectRequest = (comment: string): { comment?: string } => {
  const trimmed = comment.trim();
  return trimmed === '' ? {} : { comment: trimmed };
};

/**
 * Whether the change set can no longer be reviewed: reviewed or stale (409), or gone (404). Only
 * the portal web service's JSON error body marks a 404 as a missing change set; any other 404,
 * such as the HTML page of a route it does not serve, is a plain failure.
 */
export const isUnreviewable = (error: ApiError): boolean =>
  error.status === 409 ||
  (error.status === 404 &&
    typeof error.details === 'object' &&
    error.details !== null &&
    Array.isArray(error.details.errors));

/** Pricing's reason an unreviewable change set cannot be reviewed. */
export const describeUnreviewable = (error: ApiError): string => {
  const messages = serverMessages((error.details && error.details.errors) || []);
  if (messages.length > 0) {
    return messages.join(' ');
  }
  return error.status === 404 ? NOT_FOUND_MESSAGE : NO_LONGER_PENDING_MESSAGE;
};

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);
